import React, { useEffect, useRef, useState, useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { stayDayOptions } from './utils'
import { IconMinus, IconPlus } from './Icon'
import { DatePicker } from '../date-picker'
import { DataType } from '../date-picker/types'
import { useTopPageStore } from '../../store/topPageStore'

const isMobileScreen = window.innerWidth < 768
const isTabletScreen = window.innerWidth < 1024 && window.innerWidth > 780
const isSmallMobileScreen = window.innerWidth <= 360

export const MainSearchModal: React.FC = () => {
  const [modalHeight, setModalHeight] = useState(0)
  const [contentHeight, setContentHeight] = useState(0)
  const modalRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const isContentOverHeight = contentHeight > modalHeight
  const { t } = useTranslation()
  const STAY_DAY_OPTIONS = useMemo(() => stayDayOptions(t), [t])
  const {
    isSearchModalOpen,
    dataSearch,
    validationErrors,
    dateSelectTab,
    selectedStartDate,
    closeSearchModal,
    handleChangeDataSearch,
    handleClearForm,
    handleChangeFromDate,
    handleStayDayRadioChange,
    handleChangeDateSelectTab,
    handleBirthdayChange,
    handleCounter,
    handleSubmit,
  } = useTopPageStore()

  const sundayOnlyDates = useMemo<DataType[]>(() => {
    const monthStart = dayjs().startOf('month')
    const daysUntilSunday = (7 - monthStart.day()) % 7
    const start = monthStart.add(daysUntilSunday, 'day')
    const end = start.add(1, 'year')
    const stayDay = Number(dataSearch.stay_day?.[0] || 7)
    const sundayDates: DataType[] = []

    let cursor = start
    while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
      if (cursor.day() === 0) {
        sundayDates.push({
          date: cursor.format('YYYY-MM-DD'),
          status: 'OK',
          price: null,
          stayDay,
        })
      }
      cursor = cursor.add(7, 'day')
    }

    return sundayDates
  }, [dataSearch.stay_day])

  useEffect(() => {
    setModalHeight(modalRef?.current?.clientHeight)
    setContentHeight(contentRef?.current?.clientHeight)
  }, [])

  const handleRenderPlanMonth = (year: number) => {
    const isCurrentYear = year === new Date().getFullYear()

    return (
      <>
        <div className="text-left text-black-900 mt-3 font-size-14">{year}</div>
        <div className="grid grid-cols-4 gap-2 mt-3">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((item) => {
            const currentMonth = new Date().getMonth() + 1
            const value = `${year}/${item > 9 ? item : `0${item}`}`
            const isActive = dataSearch.plan_month?.includes(value)
            const isDisabled = item < currentMonth && isCurrentYear
            if (isCurrentYear && currentMonth > 4 && item <= 4) return null
            if (isCurrentYear && currentMonth > 8 && item <= 8) return null
            return (
              <button
                key={item}
                disabled={isDisabled}
                onClick={() => handleChangeDataSearch('plan_month', `${value}`, 'option')}
                className={`py-2 px-4 border rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'background-green-primary text-white'
                    : isDisabled
                      ? 'bg-white cursor-not-allowed'
                      : 'bg-white text-black-700 border-black-900'
                }`}
                style={{
                  borderColor: isActive ? '#189855' : isDisabled ? '#D1D5DB' : undefined,
                  color: isActive ? undefined : isDisabled ? '#D1D5DB' : undefined,
                }}
              >
                {t('month.abbr_month_names.' + item)}
              </button>
            )
          })}
        </div>
      </>
    )
  }

  // 子どもカウンター共通コンポーネント
  const ChildCounter: React.FC<{
    label: string
    value: number
    onChange: (type: 'up' | 'down') => void
    minValue?: number
  }> = ({ label, value, onChange, minValue = 0 }) => {
    return (
      <div className="w-full grid grid-cols-12 mt-3 mb-3">
        <div className={`${isSmallMobileScreen ? 'col-start-1 col-span-6' : 'col-start-1 col-span-8'} text-left`}>
          <div>{label}</div>
        </div>
        <div
          className={`${
            isSmallMobileScreen ? 'col-start-7 col-span-6' : 'col-start-9 col-span-4'
          } flex items-center gap-2 h-8 ml-auto`}
        >
          <button
            onClick={() => onChange('down')}
            className="div rounded-full cursor-pointer select-none disabled:opacity-30"
            disabled={value <= minValue}
          >
            <IconMinus />
          </button>
          <div className="mt-1 w-6 text-center">{value}</div>
          <button onClick={() => onChange('up')} className="all-unset cursor-pointer">
            <IconPlus />
          </button>
        </div>
      </div>
    )
  }

  // 年・月・日リストを用意
  const now = new Date()
  const currentYear = now.getFullYear()
  const years = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => String(currentYear - i))
  const months = Array.from({ length: 12 }, (_, i) => String(i + 1))
  const days = Array.from({ length: 31 }, (_, i) => String(i + 1))

  // 子どもフォーム部分
  const ChildrenForm = () => {
    return (
      <>
        {/* 新しい統合形式「留学あり」 */}
        <ChildCounter
          label={t('plan_detail.reservation.modal.people.study_abroad')}
          value={dataSearch.number_of_study_abroad || 0}
          onChange={(type) => handleCounter('number_of_study_abroad', type)}
          minValue={1}
        />
        {Array.from({ length: dataSearch.number_of_study_abroad }).map((_, idx) => {
          const birthday = dataSearch.birthdays[idx] || { year: '', month: '', day: '' }
          return (
            <div key={idx} className="mb-2">
              <div className="text-sm mb-1">
                {t('plan_detail.reservation.modal.people.study_abroad_birthdate', { count: idx + 1 })}
                <span className="text-red-500">*</span>
              </div>
              <div className="flex">
                <select
                  className="date-selection-year text-sm text-gray-900 pl-2 py-3 rounded-lg bg-gray-50 select-check date-selection md:w-[143px] mt-2"
                  style={{ borderColor: validationErrors.childBirthdate ? 'red' : 'rgb(229, 231, 235)' }}
                  value={birthday.year}
                  onChange={(e) => handleBirthdayChange(idx, 'year', e.target.value)}
                >
                  <option value="">生年</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                <span className="col-start-11 col-span-2 mt-auto px-2">年</span>

                <select
                  className="date-selection-month text-sm text-gray-900 pl-2 py-3 rounded-lg bg-gray-50 mt-2 select-check date-selection w-[70px]"
                  style={{ borderColor: validationErrors.childBirthdate ? 'red' : 'rgb(229, 231, 235)' }}
                  value={birthday.month}
                  onChange={(e) => handleBirthdayChange(idx, 'month', e.target.value)}
                >
                  <option value="">月</option>
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <span className="col-start-11 col-span-2 pl-2 ml-1 mt-auto px-2 md:ml-0">月</span>

                <select
                  className="date-selection-day text-sm text-gray-900 pl-2 py-3 rounded-lg bg-gray-50 mt-2 select-check date-selection w-[70px]"
                  style={{ borderColor: validationErrors.childBirthdate ? 'red' : 'rgb(229, 231, 235)' }}
                  value={birthday.day}
                  onChange={(e) => handleBirthdayChange(idx, 'day', e.target.value)}
                >
                  <option value="">日</option>
                  {days.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
                <span className="col-start-11 col-span-2 pl-2 ml-4 mt-auto md:pl-2 md:ml-0">日</span>
              </div>
            </div>
          )
        })}

        {/* 留学なし（未就学児・小学生） */}
        <ChildCounter
          label={t('plan_detail.reservation.modal.people.not_and_toddler')}
          value={dataSearch.number_of_preschoolers_1}
          onChange={(type) => handleCounter('number_of_preschoolers_1', type)}
        />
        <ChildCounter
          label={t('plan_detail.reservation.modal.people.not_and_elementary')}
          value={dataSearch.number_of_students_1}
          onChange={(type) => handleCounter('number_of_students_1', type)}
        />
      </>
    )
  }

  const handleApplySearch = () => {
    const isValid = handleSubmit()
    if (!isValid) {
      contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  if (!isSearchModalOpen) return null

  return (
    <>
      <div
        className="modal-search-backdrop fixed top-0 left-0 right-0 z-50 w-full overflow-x-hidden md:inset-0 max-h-full h-full justify-center items-center flex"
        role="dialog"
      >
        <div
          className="relative w-full"
          style={{
            width: isMobileScreen ? '100%' : '465px',
            display: isMobileScreen ? 'block' : 'flex',
            alignItems: 'normal',
            height: isMobileScreen ? '100%' : isTabletScreen ? '77%' : '86%',
            animation: 'slide-down 0.4s ease-in-out forwards',
            borderRadius: isMobileScreen ? '0px' : '22px',
          }}
        >
          <div
            className="shadow-sm dark:bg-gray-700 "
            ref={modalRef}
            style={{
              height: '100%',
              width: '100%',
              backgroundColor: isMobileScreen ? 'white' : 'transparent',
              borderRadius: isMobileScreen ? '0px' : '22px',
              marginLeft: isMobileScreen ? '0px' : 'auto',
              marginRight: isMobileScreen ? '0px' : 'auto',
            }}
          >
            <div
              className="flex bg-white items-center justify-between p-4 rounded-t"
              style={{
                borderRadius: isMobileScreen ? '0' : '16px 16px 0px 0px',
                height: isSmallMobileScreen ? '12%' : '8%',
              }}
            >
              <h3 className="mt-1 font-semibold dark:text-white text-black font-size-16px">{t('top.modal_search.header')}</h3>

              <button
                className="text-gray-900 bg-transparent hover:bg-gray-200 rounded-lg text-sm ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white p-1.5"
                type="button"
                onClick={closeSearchModal}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    clipRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
            </div>
            {validationErrors.minStudyAbroad && (
              <div className="bg-white px-3 pb-3">
                <div
                  id="min-children-notice"
                  style={{
                    color: '#DC2626',
                    border: '1px solid #FCA5A5',
                    borderRadius: '8px',
                    fontSize: '12px',
                    padding: '10px',
                    backgroundColor: '#FEF2F2',
                  }}
                >
                  {t('plan_detail.reservation.modal.people.min_children_notice')}
                </div>
              </div>
            )}
            {validationErrors.childBirthdate && (
              <div className="bg-white px-3 pb-3">
                <div
                  id="birthdate-notice"
                  style={{
                    color: '#007CFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '12px',
                    padding: '10px',
                    backgroundColor: '#EFF6FE',
                  }}
                >
                  {t('plan_detail.reservation.modal.people.birthdate_notice')}
                </div>
              </div>
            )}
            <div
              ref={contentRef}
              className="space-y-6 md:mb-0 bg-white"
              style={{
                height: isSmallMobileScreen
                  ? '80dvh'
                  : isMobileScreen
                    ? '83dvh'
                    : isContentOverHeight
                      ? '80%'
                      : isTabletScreen
                        ? '75%'
                        : 'unset',
                maxHeight: isSmallMobileScreen ? '76%' : '80%',
                overflow: 'auto',
                padding: isSmallMobileScreen ? '10px' : '1rem',
              }}
            >
              <div className="w-full grid grid-cols-12">
                <div className="col-span-8">
                  <div className="title font-bold mb-4 text-left text-black-700">
                    {t('plan_detail.reservation.modal.people.title')}
                  </div>
                </div>
                <div className={`${isSmallMobileScreen ? 'col-start-1 col-span-6' : 'col-start-1 col-span-8'} text-left`}>
                  <div className="title font-bold">{t('plan_detail.reservation.modal.people.adult')}</div>
                </div>
                <div
                  className={`${
                    isSmallMobileScreen ? 'col-start-7 col-span-6' : 'col-start-9 col-span-4'
                  } flex items-cennter gap-2 h-8 ml-auto`}
                >
                  <button
                    onClick={() => handleChangeDataSearch('maximum_number_of_adults', '', 'down')}
                    className="all-unset div rounded-full cursor-pointer select-none rounded-bl-full"
                  >
                    <IconMinus />
                  </button>
                  <div className="text-center mt-1 w-6">{dataSearch.maximum_number_of_adults}</div>
                  <button
                    className="all-unset rounded-full cursor-pointer rounded-bl-full md:ml-0"
                    onClick={() => handleChangeDataSearch('maximum_number_of_adults', '', 'up')}
                  >
                    <IconPlus />
                  </button>
                </div>
              </div>
              <div className="w-full grid grid-cols-12">
                <div className={`${isSmallMobileScreen ? 'col-start-1 col-span-6' : 'col-start-1 col-span-8'} text-left`}>
                  <div className="title font-bold">{t('plan_detail.reservation.modal.people.child')}</div>
                </div>
              </div>
              <ChildrenForm />

              <div className="w-full grid grid-cols-12 mt-5">
                <div className="col-span-12 mt-3">
                  <div className="relative tab-group mb-4">
                    <div className="flex bg-gray-100 p-1 relative rounded-12" role="tablist">
                      <div
                        className={`absolute top-0.5 left-0.5 h-8 w-[calc(50%-0.25rem)] bg-white rounded-md shadow-xs transition-all duration-300 tab-indicator z-0 ${
                          dateSelectTab === 'month' ? 'translate-x-full' : 'translate-x-0'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => handleChangeDateSelectTab('date')}
                        className={`tab-link text-sm inline-block py-2 px-4 text-slate-800 transition-all duration-300 relative z-10 mr-1 w-1/2 ${
                          dateSelectTab === 'date' ? 'active bg-white rounded-12' : ''
                        }`}
                        role="tab"
                        aria-selected={dateSelectTab === 'date'}
                      >
                        日程選択
                      </button>
                      <button
                        type="button"
                        onClick={() => handleChangeDateSelectTab('month')}
                        className={`tab-link text-sm inline-block py-2 px-4 text-slate-800 transition-all duration-300 relative z-10 w-1/2 ${
                          dateSelectTab === 'month' ? 'active bg-white rounded-12' : ''
                        }`}
                        role="tab"
                        aria-selected={dateSelectTab === 'month'}
                      >
                        月選択
                      </button>
                    </div>
                  </div>
                  {dateSelectTab === 'date' ? (
                    <div className="mt-3">
                      <div className="col-span-8 text-left">
                        <div className="title font-semibold text-gray-500">{t('top.modal_search.stay_day.title')}</div>
                      </div>
                      <div className="title text-left text-gray-500 text-sm mt-2">{t('top.modal_search.stay_day.subtitle')}</div>
                      <div className="flex gap-1 mt-3 mb-3">
                        {STAY_DAY_OPTIONS.map((item) => {
                          const selectedStayDay = dataSearch.stay_day?.[0] || '7'
                          const isActive = selectedStayDay === item.value
                          return (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => handleStayDayRadioChange(item.value)}
                              className={`w-full text-center py-2 px-1 border rounded-lg text-xs transition-colors ${
                                isActive ? 'background-green-primary text-white' : 'bg-white text-black-700 border-black-900'
                              }`}
                              style={{
                                borderColor: isActive ? '#189855' : undefined,
                              }}
                            >
                              {item.name}
                            </button>
                          )
                        })}
                      </div>
                      <DatePicker
                        onChangeDataSearch={(_key, value) => handleChangeFromDate(value)}
                        initDate={selectedStartDate}
                        stayDay={Number(dataSearch.stay_day?.[0] || 7)}
                        data={sundayOnlyDates}
                        full={false}
                        showDayIcon={false}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="col-span-8 text-left">
                        <div className="title font-semibold text-gray-500">{t('top.modal_search.stay_day.title')}</div>
                      </div>
                      <div className="title text-left text-gray-500 text-sm mt-2">{t('top.modal_search.stay_day.subtitle')}</div>
                      <div className="flex gap-1 mt-3">
                        {STAY_DAY_OPTIONS.map((item) => {
                          const isActive = dataSearch.stay_day.includes(item.value)
                          return (
                            <button
                              key={item.id}
                              onClick={() => handleChangeDataSearch('stay_day', item.value, 'option')}
                              className={`w-full text-center py-2 px-1 border rounded-lg text-xs transition-colors ${
                                isActive ? 'background-green-primary text-white' : 'bg-white text-black-700 border-black-900'
                              }`}
                              style={{
                                borderColor: isActive ? '#189855' : undefined,
                              }}
                            >
                              {item.name}
                            </button>
                          )
                        })}
                      </div>
                      <div className="title text-left text-gray-900 text-sm mt-5">{t('top.modal_search.plan_month.title')}</div>
                      {handleRenderPlanMonth(currentYear)}
                      {handleRenderPlanMonth(currentYear + 1)}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div
              className="flex w-full items-center p-4 space-x-2 rounded-b bg-white"
              style={{
                borderRadius: isMobileScreen ? '0px' : '0px 0px 16px 16px',
                position: isMobileScreen || isContentOverHeight ? 'fixed' : 'unset',
                height: isMobileScreen || isTabletScreen ? '12%' : '10%',
              }}
            >
              <button
                className="w-1/2 md:w-1/2 text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-hidden focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600 py-2.5"
                style={{
                  padding: isSmallMobileScreen ? '0.4rem 1rem' : '0.7rem 1rem',
                }}
                onClick={handleClearForm}
                type="button"
              >
                {t('top.filtering.button.clear')}
              </button>

              <button
                className="w-1/2 md:w-1/2 background-green-primary text-white focus:ring-4 focus:outline-hidden focus:ring-blue-300 font-medium rounded-lg text-sm px-5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 py-2.5"
                onClick={handleApplySearch}
                style={{
                  padding: isSmallMobileScreen ? '0.4rem 1rem' : '0.7rem 1rem',
                }}
                type="button"
              >
                {t('top.filtering.button.apply')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
